import { FetchInfiniteQueryOptions, GetNextPageParamFunction, QueryClient } from "@tanstack/react-query";
import type {
  InfiniteQueryMethodForModel,
  ModelName,
  ParamsForMethod,
  ReturnForMethod,
  TellescopeQueryKey,
  SupportedSession,
} from "../../types";
import { buildTellescopeQueryKey } from "../../keys";
import { executeTellescopeApiMethod } from "../execute-tellescope-api-method";
import { TellescopeDefinedInfiniteQueryOptionKeys } from "../../hooks/types";

type PrefetchTellescopeInfiniteQueryOptions<
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> = Omit<
  FetchInfiniteQueryOptions<
    ReturnForMethod<Model, Method>,
    TError,
    TData,
    TellescopeQueryKey<Model, Method>,
    TPageParam
  >,
  TellescopeDefinedInfiniteQueryOptionKeys | "initialPageParam" | "getNextPageParam"
>;

/**
 * Arguments for prefetching an infinite Tellescope query.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TPageParam - The page parameter type
 * @template TError - The error type (defaults to Error)
 * @template TData - The transformed data type (defaults to the method's return type)
 */
export interface PrefetchTellescopeInfiniteQueryArgs<
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> extends PrefetchTellescopeInfiniteQueryOptions<Model, Method, TPageParam, TError, TData> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The base request parameters (will be merged with page params in queryFn) */
  request: ParamsForMethod<Model, Method>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The Tellescope session instance */
  session: SupportedSession;
  /** Optional function to merge page param into request */
  paginateRequest?: (pageParam: TPageParam, request: ParamsForMethod<Model, Method>) => ParamsForMethod<Model, Method>;
  /** The initial page parameter value */
  initialPageParam: TPageParam;
  /** Function to get the next page parameter from the last page */
  getNextPageParam: GetNextPageParamFunction<TPageParam, ReturnForMethod<Model, Method>>;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}

/**
 * Prefetches an infinite query from the Tellescope API using the query client.
 * This is a lower-level function that wraps `queryClient.prefetchInfiniteQuery` with Tellescope-specific configuration.
 * Prefetching allows you to fetch paginated data before it's needed, improving perceived performance.
 *
 * @param args - The prefetch infinite query arguments
 * @returns A promise that resolves when the query has been prefetched
 *
 * @example
 * ```tsx
 * await prefetchTellescopeInfiniteQuery({
 *   queryClient,
 *   session,
 *   model: "users",
 *   method: "getAll",
 *   request: { filter: { active: true } },
 *   initialPageParam: undefined,
 *   getNextPageParam: (lastPage) => lastPage[lastPage.length - 1]?.id,
 * });
 * ```
 */
export const prefetchTellescopeInfiniteQuery = <
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
>({
  model,
  method,
  request,
  queryClient,
  session,
  paginateRequest,
  initialPageParam,
  getNextPageParam,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: PrefetchTellescopeInfiniteQueryArgs<Model, Method, TPageParam, TError, TData>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey });
  return queryClient.prefetchInfiniteQuery<
    ReturnForMethod<Model, Method>,
    TError,
    TData,
    TellescopeQueryKey<Model, Method>,
    TPageParam
  >({
    ...tanstackOptions,
    queryKey,
    initialPageParam,
    getNextPageParam,
    queryFn: ({ pageParam }) =>
      executeTellescopeApiMethod<Model, Method, false>({
        session,
        model,
        method,
        // @ts-expect-error - TypeScript doesn't infer pageParam type from generic
        request: paginateRequest ? paginateRequest(pageParam, request) : request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      }),
  });
};
